import { useLocalStorage } from '@mantine/hooks';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCurrentClient } from 'src/clients/context/CurrentClientProvider';
import { api } from 'src/common/utils/api';
import {
  Frequency,
  useTotalForFrequency,
  calculateAnnualAmount,
  calculateMonthlyAmount,
} from '../utils';
import { Dispatch, SetStateAction } from 'react';

export type IncomeFrequency = 'Monthly' | 'Weekly' | 'Annually' | 'One-off';
export type IncomeType =
  | 'Income'
  | 'Benefit'
  | 'Employment'
  | 'ShareOrDividend'
  | 'Rental'
  | 'Pension';

export type ApiIncome = {
  id: string;
  description: string;
  frequency: IncomeFrequency;
  amountPreTax: number;
  amountPostTax: number;
  incomeType: IncomeType;
  clientId: string;
};

export type Income = ApiIncome & {
  amountPreTaxAnnually: number;
  amountPostTaxAnnually: number;
  amountPreTaxMonthly: number;
  amountPostTaxMonthly: number;
};

export type EditableIncome = Omit<ApiIncome, 'id'> & {
  id?: string;
};

type UseIncomesReturnType = {
  isLoading: boolean;
  incomesData: Income[] | undefined;
  upsertIncomes: (items: EditableIncome[]) => Promise<void>;
  deleteIncomes: (ids: string[]) => Promise<void>;
  frequency: Frequency;
  setFrequency: Dispatch<SetStateAction<Frequency>>;
  totalPreTax: number;
  totalPostTax: number;
};

export default function useIncomes(): UseIncomesReturnType {
  const { clientId } = useCurrentClient();
  const queryClient = useQueryClient();
  const [frequency, setFrequency] = useLocalStorage<Frequency>({
    key: 'cashflow-incomes-frequency',
    defaultValue: 'monthly',
  });

  const queryKey = ['clients', clientId, 'incomes'];

  const { data, isLoading } = useQuery<ApiIncome[]>({
    queryKey,
  });

  const incomesData = data?.map(income => ({
    ...income,
    amountPreTaxAnnually: calculateAnnualAmount(income.amountPreTax, income.frequency),
    amountPostTaxAnnually: calculateAnnualAmount(income.amountPostTax, income.frequency),
    amountPreTaxMonthly: calculateMonthlyAmount(income.amountPreTax, income.frequency),
    amountPostTaxMonthly: calculateMonthlyAmount(income.amountPostTax, income.frequency),
  }));

  const { mutateAsync: upsertMutation } = useMutation({
    mutationFn: (items: EditableIncome[]) =>
      api.put(
        `clients/${clientId}/incomes/batch`,
        items.map(item => ({ ...item, clientId })),
      ),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  const upsertIncomes = async (items: EditableIncome[]) => {
    await upsertMutation(items);
  };

  const { mutateAsync: deleteMutation } = useMutation({
    mutationFn: (ids: string[]) => api.post(`clients/${clientId}/incomes/delete-request`, ids),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });

  const deleteIncomes = async (ids: string[]) => {
    await deleteMutation(ids);
  };

  const totalPreTax = useTotalForFrequency({
    items: incomesData,
    frequency,
    frequencyField: 'frequency',
    amountField: 'amountPreTax',
  });

  const totalPostTax = useTotalForFrequency({
    items: incomesData,
    frequency,
    frequencyField: 'frequency',
    amountField: 'amountPostTax',
  });

  return {
    isLoading,
    incomesData,
    upsertIncomes,
    deleteIncomes,
    frequency,
    setFrequency,
    totalPreTax,
    totalPostTax,
  };
}
